// ─────────────────────────────────────────────────────────────────────────────
// Settings → Workflow Configuration → Workflow Engine (ADR-0011)
//
// The workflow engine is a PLATFORM service, not a Sample Preparation feature.
// Five modules share it. What differs per module is exactly three things:
//   · scopeLabel — the key that SELECTS which workflow applies
//   · palette    — which component types are legal (designer, next phase)
//   · unit       — what actually flows down the graph
//
// Adding a module is a data change here plus a row in dbo.AI_Workflow — no new
// code path. These ids match dbo.AI_Workflow.ModuleId (CHECK-constrained) and
// Application/Workflow/WorkflowModels.cs; never rename one on its own.
// ─────────────────────────────────────────────────────────────────────────────

export type WorkflowModuleId =
  | 'sampling' | 'registration' | 'prep' | 'analysis' | 'reporting';

export interface WorkflowModuleMeta {
  id: WorkflowModuleId;
  label: string;
  icon: string;
  /** What the "Applies to" column MEANS for this module. */
  scopeLabel: string;
  /** What flows down the graph. */
  unit: string;
  /** The entry variants the lab recognises for this module. */
  variants: string[];
  /**
   * True when the engine executes this module's workflows end-to-end today.
   * Sampling / Registration / Reporting are configurable and validated but not
   * yet routed — the grid labels them "Design only" so the screen never
   * over-claims what the engine does (ADR-0011, Consequences).
   */
  routes: boolean;
}

export const WORKFLOW_MODULES: Record<WorkflowModuleId, WorkflowModuleMeta> = {
  sampling: {
    id: 'sampling', label: 'Sampling Management', icon: 'ti-route',
    scopeLabel: 'Project / route', unit: 'Scheduled visit → container',
    variants: ['Flutter (ALPACA Field)', 'Manual'], routes: false,
  },
  registration: {
    id: 'registration', label: 'Sample Registration', icon: 'ti-clipboard-list',
    scopeLabel: 'Client / project', unit: 'Job → sample',
    variants: ['Pre-Login', 'Regular Login'], routes: false,
  },
  prep: {
    id: 'prep', label: 'Sample Preparation', icon: 'ti-flask',
    scopeLabel: 'Test × Matrix × Method', unit: 'Sample → prep batch',
    variants: ['Method driven'], routes: true,
  },
  analysis: {
    id: 'analysis', label: 'Analysis', icon: 'ti-microscope',
    scopeLabel: 'Test × Method', unit: 'Sample → analytical batch',
    variants: ['SDMS Entry', 'Direct Result Entry'], routes: true,
  },
  reporting: {
    id: 'reporting', label: 'Reporting', icon: 'ti-file-text',
    scopeLabel: 'Report type / client', unit: 'Job → report',
    variants: ['COA', 'EDD', 'Invoice', 'Batch report'], routes: false,
  },
};

export const WORKFLOW_MODULE_IDS = Object.keys(WORKFLOW_MODULES) as WorkflowModuleId[];

/** One row in the catalog grid. Mirrors Application/Workflow WorkflowListRow. */
export interface WorkflowListRow {
  oid: string;
  moduleId: WorkflowModuleId;
  workflowName: string;
  workflowCode: string | null;
  description: string | null;
  scopeTest: string | null;
  scopeMatrix: string | null;
  scopeMethod: string | null;
  scopeLabel: string | null;
  version: number;
  status: 'Draft' | 'Active' | 'Inactive';
  /** Second level of the module tree — derived from the graph server-side, so a
   *  new prep type or report type grows its own branch with no UI change. */
  subGroup: string | null;
  nodeCount: number;
  edgeCount: number;
  createdAt: string;
  modifiedAt: string | null;
  modifiedBy: string | null;
}

/** One configured preparation STEP — the Prep Methods view. */
export interface WorkflowPrepRow {
  workflowOid: string;
  nodeKey: string;
  matrix: string | null;
  test: string | null;
  methodNumber: string | null;
  prepMethod: string | null;
  prepType: string | null;
  tier: number | null;
  testCode: string | null;
  status: string;
  version: number;
  lastUpdatedBy: string | null;
  lastUpdatedDate: string | null;
}

export interface WorkflowNode {
  nodeKey: string;
  nodeType: string;
  nodeName: string | null;
  posX: number;
  posY: number;
  propsJson: string | null;
  prepTier: number | null;
}

export interface WorkflowEdge {
  fromNodeKey: string;
  toNodeKey: string;
  portKey: string;
}

export interface WorkflowDetail extends Omit<WorkflowListRow, 'nodeCount' | 'edgeCount'> {
  publishedAt: string | null;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/**
 * The "Applies to" string for a row — the scope KEY, rendered per module.
 * Prep shows Matrix × Test × Method; Analysis shows Test × Method; the other
 * modules key off a free-text scope. This is the single place that decision
 * lives, so the grid and the designer can never disagree about it.
 */
export function scopeOf(row: Pick<WorkflowListRow,
  'moduleId' | 'scopeTest' | 'scopeMatrix' | 'scopeMethod' | 'scopeLabel' | 'workflowName'>): string {
  switch (row.moduleId) {
    case 'prep':
      return [row.scopeMatrix, row.scopeTest, row.scopeMethod].filter(Boolean).join(' × ') || '—';
    case 'analysis':
      return [row.scopeTest, row.scopeMethod].filter(Boolean).join(' × ') || '—';
    default:
      return row.scopeLabel || row.scopeTest || row.workflowName;
  }
}
